import * as React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";

import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

export interface CallDisposition {
	label: string;
	count: number;
	color?: "primary" | "secondary" | "success" | "warning" | "error" | "info";
}

export interface CallDispositionsCardProps {
	title: string;
	subtitle?: string;
	dispositions: CallDisposition[];
	isLoading?: boolean;
}

const getDotColor = (color: CallDisposition["color"]) => {
	const colorMap = {
		primary: "primary.main",
		secondary: "secondary.main",
		success: "success.main",
		warning: "warning.main",
		error: "error.main",
		info: "info.main",
	};
	return colorMap[color || "primary"];
};

export function CallDispositionsCard({
	title,
	subtitle,
	dispositions,
	isLoading,
}: CallDispositionsCardProps): React.JSX.Element {
	return (
		<Card sx={{ height: dispositions.length > 0 ? "100%" : "auto" }}>
			<CardContent sx={{ p: 2, pb: "12px !important" }}>
				<Box sx={{ mb: 1.5 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.25 }}>
						{title}
					</Typography>
					{subtitle && (
						<Typography variant="caption" color="text.secondary">
							{subtitle}
						</Typography>
					)}
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						ml: 1,
						overflow: "auto",
						maxHeight: { lg: "150px", md: "150px", sm: "150px", xs: "350px" },
					}}
				>
					{isLoading ? (
						<>
							{Array.from({ length: dispositions.length || 1 }).map((_, index) => (
								<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<CustomSkeleton variant="rounded" width={8} height={8} />
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<CustomSkeleton variant="rounded" width={100} height={14} />
										<Typography variant="caption" fontWeight={500}>
											-
										</Typography>
										<CustomSkeleton variant="rounded" width={20} height={14} />
									</Box>
								</Box>
							))}
						</>
					) : dispositions.length > 0 ? (
						dispositions.map((disposition, index) => (
							<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: "50%",
										bgcolor: getDotColor(disposition.color),
										flexShrink: 0,
									}}
								/>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Typography variant="caption" fontWeight={500}>
										{disposition.label}
									</Typography>
									<Typography variant="caption" fontWeight={500}>
										-
									</Typography>
									<Typography variant="caption" fontWeight={600}>
										({disposition.count})
									</Typography>
								</Box>
							</Box>
						))
					) : (
						<Typography variant="caption" ml={-1} textAlign="center" fontWeight={500}>
							N/A
						</Typography>
					)}
				</Box>
			</CardContent>
		</Card>
	);
}
